import * as THREE from 'three';
import { PRESETS } from './constants';
import { state } from './state';
import { refs } from './refs';
import { buildBook } from './book';
import { applyCamera, applySpin, setViewMode, resize } from './camera';
import { applyCalibration, syncCalibUI, setPresetCustom } from './calibration';
import { refreshBackdrop } from './backdrop';
import { exportFrame } from './export';
import { loader, loadImageAsTexture } from './textureLoader';
import { loadDemoCover } from './demo';

const $ = (id: string) => document.getElementById(id)!;

// ─── Controls ──────────────────────────────────────────────────────
function bindSlider(
  id: string,
  valId: string,
  fn: (v: number) => void,
  fmt: (v: number) => string = v => v.toFixed(2),
): void {
  const s = $(id) as HTMLInputElement,
    out = $(valId);
  s.addEventListener('input', () => {
    const v = parseFloat(s.value);
    out.textContent = fmt(v);
    fn(v);
  });
}

export function initControls(): void {
  bindSlider('bulgeSlider', 'bulgeVal', v => {
    state.bulgeF = v;
    buildBook();
  });
  bindSlider('grooveSlider', 'grooveVal', v => {
    state.grooveF = v;
    buildBook();
  });
  bindSlider('grooveWSlider', 'grooveWVal', v => {
    state.grooveWF = v;
    buildBook();
  });
  bindSlider('bowSlider', 'bowVal', v => {
    state.bowF = v;
    buildBook();
  });
  bindSlider('warpSlider', 'warpVal', v => {
    state.warpF = v;
    buildBook();
  });
  bindSlider('sqSlider', 'sqVal', v => {
    state.squareF = v;
    buildBook();
  });
  bindSlider('reflSlider', 'reflVal', v => {
    state.reflInt = v;
    buildBook();
  });
  bindSlider(
    'tempSlider',
    'tempVal',
    v => {
      state.tempK = v;
      setPresetCustom();
      applyCalibration();
    },
    v => String(Math.round(v)),
  );
  bindSlider(
    'tintSlider',
    'tintVal',
    v => {
      state.tint = v;
      setPresetCustom();
      applyCalibration();
    },
    v => String(Math.round(v)),
  );
  bindSlider('expSlider', 'expVal', v => {
    state.exposure = v;
    setPresetCustom();
    applyCalibration();
  });

  // Preset dropdown
  (function initPresets() {
    const sel = $('presetSel') as HTMLSelectElement;
    PRESETS.forEach((p, i) => {
      const o = document.createElement('option');
      o.value = String(i);
      o.textContent = p.name;
      sel.appendChild(o);
    });
    const c = document.createElement('option');
    c.value = 'custom';
    c.textContent = 'Custom';
    sel.appendChild(c);
    sel.addEventListener('change', () => {
      if (sel.value === 'custom') return;
      const p = PRESETS[+sel.value];
      state.tempK = p.tempK;
      state.tint = p.tint;
      state.exposure = p.exposure;
      syncCalibUI();
      applyCalibration();
    });
  })();

  // Copy settings -> paste into the PRESETS array
  $('copyBtn').addEventListener('click', () => {
    const s = `{ name: 'My light', tempK: ${Math.round(state.tempK)}, tint: ${Math.round(state.tint)}, exposure: ${Number(state.exposure).toFixed(2)} },`;
    $('settingsOut').textContent = s;
    navigator.clipboard && navigator.clipboard.writeText(s).catch(() => {});
  });

  // Rotation mode toggle
  document.querySelectorAll<HTMLButtonElement>('.preset-btn.rotmode').forEach(btn => {
    btn.addEventListener('click', () => {
      document
        .querySelectorAll('.preset-btn.rotmode')
        .forEach(b => b.classList.toggle('active', b === btn));
      setViewMode(btn.dataset.rot as 'turntable' | 'orbit');
    });
  });

  ['dimW', 'dimH', 'dimS'].forEach(id => {
    $(id).addEventListener('change', () => {
      state.W = parseFloat(($('dimW') as HTMLInputElement).value) || 6;
      state.H = parseFloat(($('dimH') as HTMLInputElement).value) || 9;
      state.S = parseFloat(($('dimS') as HTMLInputElement).value) || 1.1;
      buildBook();
      applyCamera();
    });
  });

  document.querySelectorAll<HTMLButtonElement>('.preset-btn[data-w]').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.preset-btn[data-w]').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      state.W = parseFloat(btn.dataset.w!);
      state.H = parseFloat(btn.dataset.h!);
      state.S = parseFloat(btn.dataset.s!);
      ($('dimW') as HTMLInputElement).value = String(state.W);
      ($('dimH') as HTMLInputElement).value = String(state.H);
      ($('dimS') as HTMLInputElement).value = String(state.S);
      buildBook();
      applyCamera();
    });
  });

  // View presets — meaning adapts to rotation mode
  const VIEWS_ORBIT: Record<string, { rx: number; ry: number }> = {
    hero: { rx: 0.16, ry: 0.62 },
    front: { rx: 0.0, ry: 0.0 },
    spine: { rx: 0.0, ry: Math.PI / 2 + 0.0001 },
    back: { rx: 0.0, ry: Math.PI },
  };
  const VIEWS_TURN: Record<string, { rx: number; spin: number }> = {
    // camera locked at ry=0; rotate the book instead
    hero: { rx: 0.16, spin: 0.55 },
    front: { rx: 0.06, spin: 0.0 },
    spine: { rx: 0.04, spin: Math.PI / 2 },
    back: { rx: 0.06, spin: Math.PI },
  };
  document.querySelectorAll<HTMLButtonElement>('.preset-btn[data-view]').forEach(btn => {
    btn.addEventListener('click', () => {
      state.autoRot = false;
      state.panX = state.panY = 0;
      if (state.viewMode === 'turntable') {
        const v = VIEWS_TURN[btn.dataset.view!];
        if (!v) return;
        state.rotX = v.rx;
        state.rotY = 0;
        state.bookSpin = v.spin;
        applySpin();
      } else {
        const v = VIEWS_ORBIT[btn.dataset.view!];
        if (!v) return;
        state.rotX = v.rx;
        state.rotY = v.ry;
      }
      applyCamera();
    });
  });

  // Panel collapse toggle
  $('togglePanel').addEventListener('click', () => {
    const collapsed = document.body.classList.toggle('collapsed');
    $('togglePanel').textContent = collapsed ? 'Show controls ▴' : 'Hide controls ▾';
    // let layout settle, then resize the renderer to the new canvas size
    requestAnimationFrame(() => {
      resize();
      applyCamera();
    });
  });
  $('barExport').addEventListener('click', () => exportFrame(1));
  $('barExportHi').addEventListener('click', () => exportFrame(2));

  $('exportBtn').addEventListener('click', () => exportFrame(1));
  $('exportHiBtn').addEventListener('click', () => exportFrame(2));

  // Backdrop controls
  function setBdActive(mode: string) {
    document
      .querySelectorAll('.preset-btn.bd')
      .forEach(b => b.classList.toggle('active', (b as HTMLElement).dataset.bd === mode));
  }
  document.querySelectorAll<HTMLButtonElement>('.preset-btn.bd').forEach(btn => {
    btn.addEventListener('click', () => {
      state.backdropMode = btn.dataset.bd as typeof state.backdropMode;
      setBdActive(state.backdropMode);
      refreshBackdrop();
    });
  });
  $('bdColor').addEventListener('input', e => {
    state.backdropColor = (e.target as HTMLInputElement).value;
    if (refs.backdropTex) {
      refs.backdropTex.dispose();
      refs.backdropTex = null;
    } // solid color overrides image
    if (state.backdropMode !== 'studio' && state.backdropMode !== 'flat') {
      state.backdropMode = 'flat';
      setBdActive('flat');
    }
    refreshBackdrop();
  });
  $('bdImg').addEventListener('change', e => {
    const f = (e.target as HTMLInputElement).files![0];
    if (!f) return;
    loader.load(URL.createObjectURL(f), tex => {
      if (refs.backdropTex) refs.backdropTex.dispose();
      refs.backdropTex = tex;
      refs.backdropTex.encoding = THREE.sRGBEncoding;
      refs.backdropTex.wrapS = refs.backdropTex.wrapT = THREE.ClampToEdgeWrapping;
      state.bdUniform = false;
      ($('bdUniform') as HTMLInputElement).checked = false; // image needs the lit plane
      if (state.backdropMode !== 'studio' && state.backdropMode !== 'flat') {
        state.backdropMode = 'flat';
      }
      setBdActive(state.backdropMode);
      refreshBackdrop();
    });
  });
  $('bdUniform').addEventListener('change', e => {
    state.bdUniform = (e.target as HTMLInputElement).checked;
    if (state.bdUniform && state.backdropMode !== 'flat') {
      state.backdropMode = 'flat';
      setBdActive('flat');
    }
    refreshBackdrop();
  });

  // Upload / drop
  $('imgInput').addEventListener('change', e => {
    const f = (e.target as HTMLInputElement).files![0];
    if (f) loadImageAsTexture(URL.createObjectURL(f));
  });
  const wrapEl = $('canvas-wrap');
  const overlay = $('drop-overlay');
  wrapEl.addEventListener('dragover', e => {
    e.preventDefault();
    overlay.classList.add('active');
  });
  wrapEl.addEventListener('dragleave', () => overlay.classList.remove('active'));
  wrapEl.addEventListener('drop', e => {
    e.preventDefault();
    overlay.classList.remove('active');
    const f = (e as DragEvent).dataTransfer!.files[0];
    if (f && f.type.startsWith('image/')) loadImageAsTexture(URL.createObjectURL(f));
  });

  // Demo cover
  $('demoBtn').addEventListener('click', loadDemoCover);
}
